/**
 * @file usePowerDistribution.js
 * @description Electrical power distribution and switchboard management
 *
 * Handles:
 * - Power source connections for equipment
 * - Switchboard classification
 * - Electrical hierarchy traversal
 * - Available power sources filtering
 */

import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';

export function usePowerDistribution() {
  // ============================================================================
  // STATE
  // ============================================================================

  const $q = useQuasar();

  /**
   * Power connection dialog visibility
   * @type {Ref<boolean>}
   */
  const powerConnectionDialogVisible = ref(false);

  /**
   * Current equipment for power connection
   * @type {Ref<Object|null>}
   */
  const powerConnectionEquipment = ref(null);

  /**
   * Available power sources (switchboards)
   * @type {Ref<Array>}
   */
  const availablePowerSources = ref([]);

  /**
   * Selected power source ID
   * @type {Ref<number|null>}
   */
  const selectedPowerSource = ref(null);

  /**
   * Switchboard classification dialog visibility
   * @type {Ref<boolean>}
   */
  const switchboardClassificationDialogVisible = ref(false);

  /**
   * Current switchboard for classification
   * @type {Ref<Object|null>}
   */
  const currentSwitchboard = ref(null);

  /**
   * Selected switchboard classification
   * @type {Ref<string|null>}
   */
  const selectedClassification = ref(null);

  /**
   * Loading state for operations
   * @type {Ref<boolean>}
   */
  const connectingPower = ref(false);

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Switchboard classification options
   */
  const switchboardClassifications = computed(() => [
    {
      value: 'main_switchboard',
      label: 'Main Switchboard (MSB)',
      description: 'Primary electrical distribution point for the site',
      icon: 'electrical_services',
      color: 'red',
    },
    {
      value: 'mechanical_switchboard',
      label: 'Mechanical Services Switchboard (MSSB)',
      description: 'Dedicated switchboard for mechanical/HVAC equipment',
      icon: 'settings',
      color: 'orange',
    },
    {
      value: 'distribution_board',
      label: 'Distribution Board (DB)',
      description: 'Secondary distribution board',
      icon: 'dashboard',
      color: 'blue',
    },
    {
      value: 'ddc_panel',
      label: 'DDC Panel',
      description: 'Direct Digital Control panel',
      icon: 'developer_board',
      color: 'purple',
    },
  ]);

  /**
   * Available power sources grouped by type
   */
  const groupedPowerSources = computed(() => {
    const groups = {
      main: [],
      mechanical: [],
      distribution: [],
      ddc: [],
    };

    availablePowerSources.value.forEach((source) => {
      switch (source.classification) {
        case 'main_switchboard':
          groups.main.push(source);
          break;
        case 'mechanical_switchboard':
          groups.mechanical.push(source);
          break;
        case 'distribution_board':
          groups.distribution.push(source);
          break;
        case 'ddc_panel':
          groups.ddc.push(source);
          break;
      }
    });

    return groups;
  });

  // ============================================================================
  // POWER CONNECTION METHODS
  // ============================================================================

  /**
   * Open power connection dialog for equipment
   * @param {Object} equipment - Equipment entity
   * @param {Array} powerSources - Available switchboards
   */
  function openPowerConnectionDialog(equipment, powerSources) {

    powerConnectionEquipment.value = equipment;
    availablePowerSources.value = powerSources;
    selectedPowerSource.value = equipment.power_source_id || null;

    powerConnectionDialogVisible.value = true;
  }

  /**
   * Close power connection dialog
   */
  function closePowerConnectionDialog() {
    powerConnectionDialogVisible.value = false;
    powerConnectionEquipment.value = null;
    selectedPowerSource.value = null;
  }

  /**
   * Save power connection
   * @param {Function} apiCallback - Async function to save via API
   * @returns {Promise<Object>} Updated equipment
   */
  async function savePowerConnection(apiCallback) {
    if (!selectedPowerSource.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a power source',
      });
      return null;
    }

    try {
      connectingPower.value = true;

      const result = await apiCallback({
        equipment_id: powerConnectionEquipment.value.id,
        power_source_id: selectedPowerSource.value,
      });

      $q.notify({
        type: 'positive',
        message: 'Power source connected successfully',
      });

      closePowerConnectionDialog();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to connect power source: ${error.message}`,
      });
      throw error;
    } finally {
      connectingPower.value = false;
    }
  }

  /**
   * Disconnect power source from equipment
   * @param {Object} equipment - Equipment entity
   * @param {Function} apiCallback - Async function to disconnect via API
   * @returns {Promise<Object>} Updated equipment
   */
  async function disconnectPowerSource(equipment, apiCallback) {
    if (!equipment.power_source_id) {
      $q.notify({
        type: 'warning',
        message: 'Equipment has no power source connected',
      });
      return null;
    }

    try {
      const confirmed = await new Promise((resolve) => {
        $q.dialog({
          title: 'Confirm Disconnection',
          message: `Disconnect ${equipment.name} from power source?`,
          cancel: true,
          persistent: false,
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false));
      });

      if (!confirmed) {return null;}

      connectingPower.value = true;

      const result = await apiCallback({
        equipment_id: equipment.id,
      });

      $q.notify({
        type: 'positive',
        message: 'Power source disconnected successfully',
      });

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to disconnect: ${error.message}`,
      });
      throw error;
    } finally {
      connectingPower.value = false;
    }
  }

  /**
   * Get available power sources for equipment based on location
   * @param {Object} equipment - Equipment entity
   * @param {Array} allSwitchboards - All switchboards in site
   * @returns {Array} Filtered power sources
   */
  function getAvailablePowerSourcesForEquipment(equipment, allSwitchboards) {
    // Filter switchboards based on:
    // 1. Same building (preferred) or site-level
    // 2. Classification appropriate for equipment type
    // 3. Not the equipment itself (if it's a switchboard)

    return allSwitchboards.filter((switchboard) => {
      // Don't allow connecting to self
      if (switchboard.id === equipment.id) {return false;}

      // Check if in same building or site-level
      const sameBuilding =
        !switchboard.building_id ||
        switchboard.building_id === equipment.building_id;

      if (!sameBuilding) {return false;}

      // Check classification appropriateness
      const classification = switchboard.classification;

      // All equipment can connect to MSB or MSSB
      if (
        classification === 'main_switchboard' ||
        classification === 'mechanical_switchboard'
      ) {
        return true;
      }

      // Distribution boards can be used for smaller equipment
      if (classification === 'distribution_board') {
        return true;
      }

      // DDC panels only for control equipment
      if (classification === 'ddc_panel') {
        return equipment.is_control_equipment === true;
      }

      return false;
    });
  }

  // ============================================================================
  // SWITCHBOARD CLASSIFICATION METHODS
  // ============================================================================

  /**
   * Open switchboard classification dialog
   * @param {Object} switchboard - Switchboard equipment
   */
  function openSwitchboardClassificationDialog(switchboard) {

    currentSwitchboard.value = switchboard;
    selectedClassification.value = switchboard.classification || null;

    switchboardClassificationDialogVisible.value = true;
  }

  /**
   * Close switchboard classification dialog
   */
  function closeSwitchboardClassificationDialog() {
    switchboardClassificationDialogVisible.value = false;
    currentSwitchboard.value = null;
    selectedClassification.value = null;
  }

  /**
   * Save switchboard classification
   * @param {Function} apiCallback - Async function to save via API
   * @returns {Promise<Object>} Updated switchboard
   */
  async function saveSwitchboardClassification(apiCallback) {
    if (!selectedClassification.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a classification',
      });
      return null;
    }

    try {
      connectingPower.value = true;

      const result = await apiCallback({
        equipment_id: currentSwitchboard.value.id,
        classification: selectedClassification.value,
      });

      $q.notify({
        type: 'positive',
        message: 'Switchboard classified successfully',
      });

      closeSwitchboardClassificationDialog();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to classify switchboard: ${error.message}`,
      });
      throw error;
    } finally {
      connectingPower.value = false;
    }
  }

  /**
   * Get classification label and icon for switchboard
   * @param {string} classification - Classification value
   * @returns {Object} Label, icon, and color
   */
  function getClassificationInfo(classification) {
    const classificationObj = switchboardClassifications.value.find(
      (c) => c.value === classification,
    );

    return (
      classificationObj || {
        value: classification,
        label: classification,
        icon: 'electrical_services',
        color: 'grey',
      }
    );
  }

  // ============================================================================
  // ELECTRICAL HIERARCHY METHODS
  // ============================================================================

  /**
   * Build electrical tree from switchboards
   * @param {Array} switchboards - All switchboards
   * @returns {Array} Tree nodes
   */
  function buildElectricalTree(switchboards) {

    // Find root nodes (no parent)
    const rootNodes = switchboards.filter((sb) => !sb.parent_switchboard_id);

    // Build tree recursively
    function buildNode(switchboard) {
      const children = switchboards
        .filter((sb) => sb.parent_switchboard_id === switchboard.id)
        .map(buildNode);

      const classificationInfo = getClassificationInfo(switchboard.classification);

      return {
        id: `switchboard-${switchboard.id}`,
        label: switchboard.name,
        type: switchboard.classification || 'switchboard',
        icon: classificationInfo.icon,
        color: classificationInfo.color,
        data: switchboard,
        children: children.length > 0 ? children : undefined,
        // Additional metadata
        connectedEquipment: switchboard.connected_equipment?.length || 0,
        capacity: switchboard.capacity || 'Unknown',
      };
    }

    return rootNodes.map(buildNode);
  }

  /**
   * Find unallocated switchboards (not in hierarchy)
   * @param {Array} allSwitchboards - All switchboards
   * @param {Array} treeNodes - Current tree nodes
   * @returns {Array} Unallocated switchboards
   */
  function findUnallocatedSwitchboards(allSwitchboards, treeNodes) {
    // Get all switchboard IDs in tree
    const idsInTree = new Set();

    function collectIds(nodes) {
      nodes.forEach((node) => {
        if (node.type.includes('switchboard') || node.type === 'ddc_panel') {
          idsInTree.add(node.data.id);
        }
        if (node.children) {
          collectIds(node.children);
        }
      });
    }

    collectIds(treeNodes);

    // Filter to switchboards not in tree
    return allSwitchboards.filter((sb) => !idsInTree.has(sb.id));
  }

  /**
   * Check if node can have child switchboards
   * @param {Object} node - Tree node
   * @returns {boolean}
   */
  function canAddChildSwitchboard(node) {
    const validTypes = ['main_switchboard', 'mechanical_switchboard', 'distribution_board'];
    return validTypes.includes(node.type);
  }

  /**
   * Get valid switchboard types that can be added as children
   * @param {Object} parentNode - Parent tree node
   * @returns {Array} Valid classification options
   */
  function getValidChildSwitchboardTypes(parentNode) {
    const allTypes = switchboardClassifications.value;

    switch (parentNode.type) {
      case 'main_switchboard':
        // MSB can have DB or DDC children
        return allTypes.filter(
          (t) => t.value === 'distribution_board' || t.value === 'ddc_panel',
        );

      case 'mechanical_switchboard':
        // MSSB can have DB or DDC children
        return allTypes.filter(
          (t) => t.value === 'distribution_board' || t.value === 'ddc_panel',
        );

      case 'distribution_board':
        // DB can only have DDC children
        return allTypes.filter((t) => t.value === 'ddc_panel');

      default:
        return [];
    }
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    powerConnectionDialogVisible,
    powerConnectionEquipment,
    availablePowerSources,
    selectedPowerSource,
    switchboardClassificationDialogVisible,
    currentSwitchboard,
    selectedClassification,
    connectingPower,

    // Computed
    switchboardClassifications,
    groupedPowerSources,

    // Power connection methods
    openPowerConnectionDialog,
    closePowerConnectionDialog,
    savePowerConnection,
    disconnectPowerSource,
    getAvailablePowerSourcesForEquipment,

    // Switchboard classification methods
    openSwitchboardClassificationDialog,
    closeSwitchboardClassificationDialog,
    saveSwitchboardClassification,
    getClassificationInfo,

    // Electrical hierarchy methods
    buildElectricalTree,
    findUnallocatedSwitchboards,
    canAddChildSwitchboard,
    getValidChildSwitchboardTypes,
  };
}
